// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormattedMessage } from 'react-intl';
import CollapsibleButton from '../../../../../elements/CollapsibleButton/CollapsibleButton';
import Loading from '../../../../../elements/Loading/Loading';
import { FormValidator } from '../../../../../utils/form';

import style from './Coupon.scss';
import icons from '../../../../../assets/styles/icons.scss';
import inputStyle from '../../../../../assets/styles/input.scss';

import type { HTTPError } from '../../../../../error';
import type { FormValidationResult } from '../../../../../utils/form';

type Props = {
  +handleApplyCoupon: (couponCode: string) => void;
  +appliedCouponCode?: string;
  +applyCouponError?: HTTPError;
  +isLoading?: boolean;
  +removeCoupon: () => void;
  +initialOpen?: boolean;
  +placeholder?: string;
};

type State = {
  coupon: {
    value: string;
    errors?: string[];
  };
};

export default class Coupon extends Component<Props, State> {
  state = {
    coupon: {
      value: '',
    },
  };
  couponValidator: FormValidator;

  constructor(props: Props) {
    super(props);
    this.couponValidator = new FormValidator({
      coupon: {
        required: 'required',
        valid: (code: number) => code === 404,
        generic: (code: number) => typeof code === 'number',
      },
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { applyCouponError, appliedCouponCode } = this.props;
    const { coupon } = this.state;
    if (!!prevProps.appliedCouponCode && !appliedCouponCode) {
      this.setState({
        coupon: {
          value: '',
        },
      });
    }
    if (!prevProps.applyCouponError && !!applyCouponError &&
      applyCouponError.data && applyCouponError.data.name === 'CouponNotFoundOrInvalidError') {
      this.setState({
        coupon: {
          value: coupon.value,
          errors: ['valid'],
        },
      });
    }
  }

  handleApplyCoupon = (e: Event) => {
    e.preventDefault();
    const { handleApplyCoupon } = this.props;
    const { coupon } = this.state;
    const errors = this.couponValidator.validateForm({
      coupon: coupon.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      handleApplyCoupon(coupon.value);
    }
  };

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({ coupon: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState({ coupon: { value } });
    }
  };

  render() {
    const { isLoading, appliedCouponCode, removeCoupon, initialOpen = false, placeholder } = this.props;
    const { coupon } = this.state;
    const couponError = coupon.errors && FormValidator.buildErrorLabel(coupon.errors, {
      required: 'newOrder.quote.coupon.error',
      valid: 'newOrder.quote.coupon.invalid',
    });

    return (
      <CollapsibleButton titleIntl="newOrder.quote.coupon.title" initialOpen={initialOpen}>
        <Fragment>
          <div className={style.couponContentWrap}>

            <FormattedMessage id="newOrder.quote.coupon.placeholder">{
              txt => <input type="text"
                            name="coupon"
                            className={classNames(inputStyle.input, style.couponInput,
                              { [inputStyle.red]: coupon && !!(coupon: any).errors },
                              { [inputStyle.lightBlue]: !!coupon.value },
                              { [style.disabled]: isLoading || appliedCouponCode })
                            }
                            disabled={appliedCouponCode}
                            value={appliedCouponCode ? '' : coupon.value}
                            onChange={this.handleChange}
                            onFocus={this.handleFocus}
                            placeholder={placeholder ? placeholder : txt}/>}
            </FormattedMessage>
            {isLoading && <Loading type="circle"
                                   color="lightBlue"
                                   className={style.loading}/>}
            {!isLoading && <FormattedMessage id={!!appliedCouponCode ?
              'newOrder.quote.coupon.applied' : 'newOrder.quote.coupon.apply'}>{
              txt => <button className={style.apply}
                             disabled={appliedCouponCode}
                             onClick={this.handleApplyCoupon}>{txt}</button>
            }</FormattedMessage>}
          </div>
          <div className={style.errorWrap}>{couponError}</div>
          {!!appliedCouponCode && <div className={style.couponBottomWrap}>
            <FormattedMessage id="newOrder.quote.coupon.success">{
              txt => <p className={style.couponSuccess}>{txt}</p>
            }</FormattedMessage>
            <div className={style.coupon}>
              <p className={style.couponCode}>{appliedCouponCode}</p>
              <i className={icons.iconCloseLight}
                 onClick={_ => removeCoupon()}/>
            </div>
          </div>}
        </Fragment>
      </CollapsibleButton>
    );
  }
}

Coupon.propTypes = {
  handleApplyCoupon: PropTypes.func.isRequired,
  appliedCouponCode: PropTypes.string,
  applyCouponError: PropTypes.object,
  isLoading: PropTypes.bool,
  removeCoupon: PropTypes.func.isRequired,
  initialOpen: PropTypes.bool,
  placeholder: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/QuoteStep/components/Coupon/Coupon.jsx